import type { QuizInfoSummary, SessionInfo } from "../results/components/TestResultsWrapper";

const RESULTS_BASE = "/api/verity/demo/results";
const BOOTSTRAP_URL = `${RESULTS_BASE}/bootstrap`;

/** The prefix every stock api-client URL carries, and the one prefix that must never be rewritten. */
const API_PREFIX = "/api/verity/";
const DEMO_PREFIX = "/api/verity/demo/";
/** Wider than `API_PREFIX`: the result view also reaches `/api/access-control/`, which is not rewritten. */
const API_ROOT = "/api/";
/** The only endpoint this page refuses to call, because its credential travels in the body. */
const REFRESH_PATH = "/api/access-control/refresh";

export type DemoResultsBootstrap = {
	instituteId: number;
	courseId: number;
	studentId: number;
	assessmentInfo: QuizInfoSummary;
	proctoringSessions: SessionInfo[];
};

function isBootstrap(value: unknown): value is DemoResultsBootstrap {
	if (typeof value !== "object" || value === null) return false;
	const candidate = value as Record<string, unknown>;
	const assessmentInfo = candidate.assessmentInfo as Record<string, unknown> | null | undefined;
	return (
		typeof candidate.instituteId === "number" &&
		typeof candidate.courseId === "number" &&
		typeof candidate.studentId === "number" &&
		typeof assessmentInfo === "object" &&
		assessmentInfo !== null &&
		typeof assessmentInfo.id === "number" &&
		Array.isArray(candidate.proctoringSessions)
	);
}

/**
 * Unlike Lumina's redeem this spends nothing — the server pins the fixture — so it is idempotent and a
 * reload costs a visitor nothing. `503` means the fixture uuid is unconfigured, which is a normal state
 * in environments that have no fixture; the caller renders "unavailable" for every failure alike.
 */
export async function fetchDemoResultsBootstrap(): Promise<DemoResultsBootstrap> {
	const response = await fetch(BOOTSTRAP_URL, {
		credentials: "omit",
		cache: "no-store",
		headers: { Accept: "application/json" },
	});
	if (!response.ok) {
		throw new Error(`GET ${BOOTSTRAP_URL} responded ${response.status}`);
	}
	const body: unknown = await response.json();
	if (!isBootstrap(body)) {
		throw new Error("The demo results bootstrap was not the expected shape.");
	}
	return body;
}

let installed = false;

/**
 * Re-points the whole api-client at `/demo/results/*` for the life of this page. No `Authorization`
 * header: the server pins the fixture, so the browser holds nothing token-like.
 *
 * **Why a `fetch` patch and not an injected client.** `ProctoringResultView` and its twenty-odd hooks
 * import the `apiClient` singleton directly and reach it through `super.fetch`, which resolves on
 * `ApiClient.prototype` and so cannot be shadowed per instance. One patch here covers every one of them.
 *
 * **Only `GET` is rewritten.** A write from this surface is a bug, and letting it fall through to the
 * stock path — where it 401s, loudly, in the console — makes the bug visible instead of quietly
 * re-routing it into the demo router. That router registers no write route at all; this rewrite is
 * plumbing, and must never be read as the access-control boundary.
 *
 * **Every same-origin API request leaves this page anonymous**, rewritten or not. The shared view calls
 * more than the routes it reads through: an analytics batch it POSTs on mount, and a privilege read
 * under `/api/access-control/` that no rewrite here would match. Those still go out and still 401 —
 * that part is deliberate, per the paragraph above — but with the `Authorization` header and cookies
 * removed, because a visitor signed in to the portal in another tab would otherwise have turned the
 * analytics batch into a real write attributed to them.
 *
 * The token refresh is the single exception, and it is refused rather than stripped: `ApiClient` answers
 * a 401 by POSTing the refresh token in a JSON *body*, which no amount of header and cookie removal
 * reaches. It is answered here, without a network call at all.
 *
 * Installing twice would wrap the wrapper, and the demo page can reach the result screen more than once
 * per page load (preview → form → result → back → …), so the second call is a no-op.
 */
export function installDemoResultsApi(): void {
	if (installed) return;
	installed = true;

	const originalFetch = window.fetch.bind(window);

	window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
		const request = input instanceof Request ? input : null;
		const method = (request?.method ?? init?.method ?? "GET").toUpperCase();
		const href = request ? request.url : input instanceof URL ? input.href : String(input);

		let url: URL;
		try {
			url = new URL(href, window.location.origin);
		} catch {
			return originalFetch(input, init);
		}
		// Anything that is not a same-origin API call is none of this patch's business.
		if (url.origin !== window.location.origin || !url.pathname.startsWith(API_ROOT)) {
			return originalFetch(input, init);
		}
		// The demo's own routes answer anonymously already, and the lead form's POST is one of them, so
		// it must reach the server exactly as written.
		if (url.pathname.startsWith(DEMO_PREFIX)) {
			return originalFetch(input, init);
		}

		// The one request that must not leave the page at all. Every 401 below sends `ApiClient` into
		// `updateTokens`, which POSTs the visitor's refresh token to this endpoint
		// (`api-client.ts:236-242`) — in the JSON body, where stripping the header and the cookies does
		// not reach it. A visitor signed in to the portal in another tab would have their tokens rotated
		// by a page that is supposed to do no auth at all.
		//
		// Answered here without touching the network. `updateTokens` treats a non-ok reply by marking the
		// payload invalid and giving up, which is the outcome we want, and it reads no body on that path.
		// The map it writes lives for this page only, so the portal's own session is untouched.
		if (url.pathname === REFRESH_PATH) {
			return Promise.resolve(new Response(null, { status: 401, statusText: "Unauthorized" }));
		}

		// `apiClient` attaches an `Authorization` header before it reaches this patch, and a visitor who
		// is also signed in to the portal in this browser would have a real one. Stripped from every
		// request below, alongside `credentials: "omit"` for cookies, so nothing this page issues can
		// carry a credential.
		const headers = new Headers(request ? request.headers : init?.headers);
		headers.delete("authorization");

		if (method === "GET" && url.pathname.startsWith(API_PREFIX)) {
			url.pathname = `${RESULTS_BASE}/${url.pathname.slice(API_PREFIX.length)}`;
			return originalFetch(url.href, { ...init, method: "GET", headers, credentials: "omit" });
		}

		// Everything else the shared view reaches for on its own: the analytics batch it POSTs on mount
		// (`useFetchProctoringResult.ts:93`), the privilege read it issues alongside
		// (`:177`, under `/api/access-control/` rather than the verity prefix, so no rewrite applies),
		// and any write a control that slipped past `readOnly` would attempt.
		//
		// These are let through rather than rewritten — this surface has no route for them, and a
		// synthetic reply here would be inventing an answer the server never gave. What must not happen
		// is that they carry the visitor's credentials: unauthenticated they are a 401 the caller
		// already handles, whereas authenticated the analytics batch becomes a real write attributed to
		// whoever happened to be signed in. Stripped, so both are anonymous either way.
		if (request) {
			return originalFetch(new Request(request, { headers, credentials: "omit" }));
		}
		return originalFetch(input, { ...init, headers, credentials: "omit" });
	};
}
